import { useEffect, useLayoutEffect, useRef, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuditStore } from '../../stores/useAuditStore';
import { useAuthStore } from '../../stores/useAuthStore';
import { useProcessingStore } from '../../stores/useProcessingStore';
import { connectSSE } from '../../api/sse';
import { createAnalysisConnection, type ConnectionStatus } from '../../api/centrifugo';
import { createConversation, fetchAuditStatus, fetchAnalysisEvents, startOwnerAudit, saveQuestionnaire } from '../../api/client';
import { useQuestionnaireStore } from '../../stores/useQuestionnaireStore';
import { useOnboardingStore } from '../../stores/useOnboardingStore';
import { QUESTIONS } from '../../data/questions';
import { mapAnswersToBackendKeys } from '../../utils/questionnaire';
import { extractThinkingSnippet, formatElapsedSeconds, humanizeToolArgs, humanizeToolName, summarizeToolResult, tallyToolResult } from '../../utils/feedSummarizer';
import { FEED_MESSAGES, ANALYSIS_STAGE_COUNT, REFINE_TOTAL_STAGES, isRefineStage } from '../../data/processing';
import { setActiveAnalysis, clearActiveAnalysis } from '../../hooks/useAnalysisConnection';
import { replayAnalysisEvents } from '../../utils/replayEvents';
import StageTimeline from './StageTimeline';
import TerminalFeed from './TerminalFeed';
import '../onboarding/onboarding.css';
// RecoveryBanner removed — see render block below.

/** Profile label persisted for cross-reload restore. The server route sets the
 * REAL conversation profile (`audit-supervisor-owner`); this is only a hint. */
const OWNER_AUDIT_PROFILE = 'audit-supervisor-owner';

let _startTime = 0;

function getElapsed(): string {
  if (!_startTime) _startTime = Date.now();
  return formatElapsedSeconds((Date.now() - _startTime) / 1000);
}

function resetTimer() {
  _startTime = Date.now();
}

/**
 * Map a wire `stage_number` to its index in the combined 11-stage pipeline.
 * The analysis pass numbers 1..7; the refine pass restarts at 1 with
 * `total_stages=4`, so its events map to `ANALYSIS_STAGE_COUNT + (n - 1)`.
 */
function combinedStageIndex(stageNumber: number, refine: boolean): number {
  return (refine ? ANALYSIS_STAGE_COUNT + stageNumber : stageNumber) - 1;
}

/** Compose a tool-start feed line: "{Label} — {args}" or "{Label} starting..." */
function toolStartMessage(name: string, input: Record<string, unknown> | undefined): string {
  const label = humanizeToolName(name);
  const detail = humanizeToolArgs(name, input);
  return detail ? `${label} — ${detail}` : `${label} starting...`;
}

/** Min gap between thinking-pulse feed entries while the LLM is mid-reason. */
const THINKING_PULSE_INTERVAL_MS = 25_000;
/** Shared throttling state for the thinking pulse — captured by closures
 * inside each Centrifugo subscription. Using module-level state keeps the
 * callbacks dependency-free (they're created on mount) while still
 * sharing the cadence so back-to-back reconnects don't double-pulse.
 */
const _thinkingState = { buffer: '', lastPulseAt: 0 };

/** Reset the thinking buffer when a "real" event fires (tool/stage) so the
 * next pulse timer starts fresh and captures only post-event reasoning.
 */
function resetThinkingPulse(): void {
  _thinkingState.buffer = '';
  _thinkingState.lastPulseAt = Date.now();
}

/** Accumulate thinking deltas and emit a short feed snippet if enough time
 * has passed since the last user-visible event. Returns the snippet to
 * append (caller writes to the feed) or null.
 */
function handleThinkingChunk(content: string): string | null {
  if (!content) return null;
  _thinkingState.buffer += content;
  const now = Date.now();
  if (now - _thinkingState.lastPulseAt < THINKING_PULSE_INTERVAL_MS) return null;
  const snippet = extractThinkingSnippet(_thinkingState.buffer);
  if (!snippet) return null;
  _thinkingState.lastPulseAt = now;
  _thinkingState.buffer = '';
  return snippet;
}

/** Compose the short subtask label shown under the stage header. */
function toolSubtaskLabel(name: string, input: Record<string, unknown> | undefined): string {
  const detail = humanizeToolArgs(name, input);
  return detail ? `${humanizeToolName(name)} · ${detail}` : humanizeToolName(name);
}

/** Tools that run as data-gathering BEFORE the backend's inferred
 * `stage_started` for "Data Ingestion". Uploaded-file ingestion, memory recall
 * and raw-data loads stream tool events ~30s before the agent loop's first
 * stage-1 marker tool, so without this the "Data Ingestion" stage sits idle
 * (pending) while the real ingestion happens, then flashes complete. */
const PRE_STAGE_TOOLS = new Set<string>([
  'ingest_uploaded_files', 'search_memory', 'load_raw_data',
  'load_analysis_data', 'get_questionnaire_answers',
  'parse_uploaded_file', 'query_uploaded_file',
]);

/** Resolve which stage index a tool_start should drive. Normally the currently
 * active stage. As a special case, pre-stage data-gathering tools light up
 * "Data Ingestion" (stages[0]) — but ONLY during the genuine pre-stage window,
 * i.e. while every stage is still pending (nothing started yet). We must NOT
 * re-activate once the pipeline has advanced: a pre-stage tool firing in the gap
 * between Data Ingestion completing and the next stage starting would otherwise
 * reopen the finished Data Ingestion and mis-attribute later tools (e.g. Reconcile
 * Spend) to it. Returns the resolved index, or -1 when nothing should be updated. */
function activeStageIdxForTool(name: string): number {
  const store = useProcessingStore.getState();
  const idx = store.stages.findIndex((s) => s.status === 'active');
  if (idx >= 0) return idx;
  if (PRE_STAGE_TOOLS.has(name) && store.stages.every((s) => s.status === 'pending')) {
    store.advanceStage(0); // "Data Ingestion" is PROCESSING_STAGES[0]
    return 0;
  }
  return -1;
}

const SSE_SILENCE_TIMEOUT = 900; // 15 min — multi-agent analysis takes 20+ min

// How often the /status backstop runs while the live socket is DOWN. Only a
// floor for that case — when the socket is up, reconciliation is event-driven.
const OFFLINE_RECONCILE_INTERVAL_MS = 120_000;

export default function ProcessingView() {
  const navigate = useNavigate();
  const { chatId: urlChatId } = useParams<{ chatId: string }>();

  const setChatId = useAuditStore((s) => s.setChatId);
  // Shown in the terminal's stat strip — how many platforms this run reads.
  const connectedCount = useAuditStore(
    (s) => s.connectedSources.filter((source) => source.status === 'connected').length,
  );
  const cancelRef = useRef<(() => void) | null>(null);
  const gotSSEEvent = useRef(false);
  const [isRestoring, setIsRestoring] = useState(false);
  const [restoreChecked, setRestoreChecked] = useState(false);
  const restoredRef = useRef(false);
  const analysisStartedRef = useRef(false);

  // ── Live-transport reconciliation ────────────────────────────────────────
  // The /status backstop used to run on a fixed 8s interval for the whole run
  // (~110 requests per audit). Centrifugo already tells us when a gap is
  // possible, so the backstop now fires on those signals instead: a socket that
  // reconnected, a subscription that could NOT replay history, and a tab coming
  // back to the foreground (hidden tabs get their timers throttled, so events
  // may have been missed). A slow timer stays as a floor while the socket is
  // down. Fewer requests also means fewer chances for a stray 401 to interrupt
  // the run.
  const liveStatusRef = useRef<ConnectionStatus>('connecting');
  const reconcileRef = useRef<() => void>(() => {});
  const handleLiveStatus = useCallback((status: ConnectionStatus) => {
    const previous = liveStatusRef.current;
    liveStatusRef.current = status;
    if (status === 'connected' && previous !== 'connected') reconcileRef.current();
  }, []);
  const handleLiveRecovery = useCallback((recovered: boolean) => {
    if (!recovered) reconcileRef.current();
  }, []);

  // Fast-forward the combined pipeline UI to wherever the backend already is.
  // `current_stage`/`current_total_stages` track whichever pass is active
  // (analysis 7-stage or refine 4-stage); refine numbers are offset past the
  // analysis stages so the bar never jumps backward on reconnect.
  const fastForwardStages = useCallback((currentStage?: number, currentTotal?: number) => {
    const store = useProcessingStore.getState();
    const reportedStage = currentStage || 0;
    const refinePass = (currentTotal || 0) === REFINE_TOTAL_STAGES;
    const combinedStage = reportedStage > 0
      ? (refinePass ? ANALYSIS_STAGE_COUNT + reportedStage : Math.min(reportedStage, ANALYSIS_STAGE_COUNT))
      : 0;
    if (combinedStage > 0) {
      for (let s = 1; s < combinedStage; s++) {
        store.completeStage(s - 1);
      }
      store.advanceStage(combinedStage - 1);
    }
  }, []);

  // Reset store before any useEffect so a stale isComplete=true from a prior
  // run doesn't fire the completion effect and navigate immediately. Use
  // useLayoutEffect (not a bare call in the render body) — the latter triggers
  // a zustand set() during React's render phase, which warns when another
  // subscriber is rendering. useLayoutEffect runs synchronously after commit,
  // before paint and before useEffect — still well ahead of the
  // completion-detection effect below, so the no-stale-advance guarantee holds.
  const pipelineInitialized = useRef(false);
  useLayoutEffect(() => {
    if (pipelineInitialized.current) return;
    pipelineInitialized.current = true;
    useProcessingStore.getState().initCombinedPipeline();
  }, []);

  const {
    stages,
    feedMessages,
    counters,
    isComplete,
    isFailed,
    errorMessage,
    advanceStage,
    completeStage,
    completeStageNoCounter,
    setStageProgress,
    appendFeedMessage,
    setCounters,
    markComplete,
    markFailed,
  } = useProcessingStore();

  const leftColRef = useRef<HTMLDivElement>(null);
  const [terminalMaxH, setTerminalMaxH] = useState<number | undefined>(undefined);

  const measureLeftCol = useCallback(() => {
    if (leftColRef.current) {
      setTerminalMaxH(leftColRef.current.offsetHeight);
    }
  }, []);

  useEffect(() => {
    measureLeftCol();
    const observer = new ResizeObserver(measureLeftCol);
    if (leftColRef.current) observer.observe(leftColRef.current);
    return () => observer.disconnect();
  }, [measureLeftCol]);

  // ── Restore from persisted events on mount (page reload / tab reopen) ──
  useEffect(() => {
    const tryRestore = async () => {
      // Guard against React StrictMode double-invocation AND any later
      // effect re-run. Claim synchronously before any await so the
      // second invocation sees the flag and bails before creating a
      // duplicate Centrifugo subscription.
      if (restoredRef.current) return;
      restoredRef.current = true;
      const ACTIVE_KEY = 'active_analysis';
      try {
        const raw = localStorage.getItem(ACTIVE_KEY);
        let stored: { chatId: string; startedAt: number } | null = null;
        try {
          stored = raw ? JSON.parse(raw) : null;
        } catch {
          stored = null;  // corrupt entry is no entry
        }

        // A chat id in the URL is the run the user asked for, so it wins over
        // `active_analysis`. That entry lives for 2h and survives a finished
        // audit, so when it was allowed to take precedence a freshly started
        // run was hijacked by the previous one: /processing/<new> restored
        // <old>, saw its deliverables, and bounced the user to that old
        // report (an under-review stub). The stored entry is only a fallback
        // for a bare /processing visit — and when it points at the SAME chat
        // it is still preferred, to keep the original startedAt so replayed
        // elapsed times stay on one clock.
        let entry: { chatId: string; startedAt: number };
        if (urlChatId) {
          entry = stored?.chatId === urlChatId
            ? stored
            : { chatId: urlChatId, startedAt: Date.now() };
        } else if (stored) {
          entry = stored;
        } else {
          restoredRef.current = false;  // no restore performed — allow new-run path
          setRestoreChecked(true);
          return;
        }

        // Always check server status — don't rely on client-side timeout alone.
        // Runs can take 30+ minutes; a client-side expiry would kill restore.
        const status = await fetchAuditStatus(entry.chatId);

        // A renderable report exists → go straight to /report.
        if (status.has_deliverables || status.has_renderable_report) {
          clearActiveAnalysis();
          navigate(`/report/${entry.chatId}`, { replace: true });
          return;
        }

        // If server says it's not running AND it's been >2 hours, expire
        if (!status.is_running && !status.is_complete && Date.now() - entry.startedAt > 2 * 60 * 60 * 1000) {
          localStorage.removeItem(ACTIVE_KEY);
          restoredRef.current = false;  // no restore — allow new-run path
          setRestoreChecked(true);
          return;
        }
        if (status.is_complete) {
          clearActiveAnalysis();
          // Only route to /report if we navigated here with a specific chatId
          // in the URL. If no urlChatId, this is a fresh /processing visit —
          // let a new run start.
          if (urlChatId) {
            setChatId(entry.chatId);
            navigate(`/report/${entry.chatId}`, { replace: true });
          } else {
            restoredRef.current = false;  // no restore, no redirect — allow new-run path
          }
          setRestoreChecked(true);
          return;
        }

        // Replay persisted events to restore UI state
        setIsRestoring(true);

        const { events } = await fetchAnalysisEvents(entry.chatId);
        if (events.length > 0) {
          // Align replayed elapsed times with the live _startTime so
          // feed entries from before and after the refresh share one
          // consistent clock.
          _startTime = entry.startedAt || Date.now();
          const { isComplete: done } = replayAnalysisEvents(events, _startTime);
          if (done) {
            clearActiveAnalysis();
            setChatId(entry.chatId);
            navigate(`/report/${entry.chatId}`, { replace: true });
            return;
          }
        }

        setChatId(entry.chatId);
        // restoredRef was already claimed at the top of this function
        setIsRestoring(false);
        setRestoreChecked(true);

        // Reconnect to Centrifugo for live updates if the run is still going.
        // Only if the main run effect hasn't already created a connection.
        if (status.is_running && import.meta.env.VITE_CENTRIFUGO_ENABLED === 'true' && !cancelRef.current) {
          _startTime = entry.startedAt || Date.now();
          fastForwardStages(status.current_stage, status.current_total_stages);

          const conn = await createAnalysisConnection(entry.chatId, {
            onStageStarted: ({ stage_number, stage_name, total_stages }) => {
              resetThinkingPulse();
              const refine = isRefineStage(stage_name, total_stages);
              useProcessingStore.getState().advanceStage(combinedStageIndex(stage_number, refine));
              useProcessingStore.getState().appendFeedMessage({
                time: getElapsed(),
                msg: refine ? stage_name : `Stage ${stage_number}: ${stage_name}`,
                type: 'highlight',
              });
            },
            onStageCompleted: ({ stage_number, stage_name }) => {
              resetThinkingPulse();
              const refine = isRefineStage(stage_name);
              const idx = combinedStageIndex(stage_number, refine);
              if (refine) {
                useProcessingStore.getState().completeStageNoCounter(idx);
              } else {
                useProcessingStore.getState().completeStage(idx);
              }
            },
            onToolStart: ({ name, input }) => {
              resetThinkingPulse();
              useProcessingStore.getState().appendFeedMessage({
                time: getElapsed(), msg: toolStartMessage(name, input), type: '',
              });
              const idx = activeStageIdxForTool(name);
              if (idx >= 0) {
                const stages = useProcessingStore.getState().stages;
                useProcessingStore.getState().updateStageSubtask(idx, toolSubtaskLabel(name, input));
                useProcessingStore.getState().setStageProgress(idx, Math.min(stages[idx].progress + 15, 95));
              }
            },
            onToolResult: ({ name, result, is_error }) => {
              resetThinkingPulse();
              const summary = summarizeToolResult(name, result, is_error);
              if (summary) {
                useProcessingStore.getState().appendFeedMessage({ time: getElapsed(), msg: summary.msg, type: summary.type });
              }
              useProcessingStore.getState().incrementCounters(tallyToolResult(name, result, is_error));
            },
            onThinking: ({ content }) => {
              const snippet = handleThinkingChunk(content);
              if (snippet) {
                useProcessingStore.getState().appendFeedMessage({
                  time: getElapsed(), msg: `… ${snippet}`, type: '',
                });
              }
            },
            onAnalysisRecovered: ({ stage_number }) => {
              useProcessingStore.getState().appendFeedMessage({
                time: getElapsed(),
                msg: `Analysis recovered — resuming from Stage ${stage_number}`,
                type: 'highlight',
              });
            },
            onDone: () => {
              // Terminal `done` for the WHOLE pipeline — the audit pass swallows
              // its own done server-side, so this only fires after refine.
              clearActiveAnalysis();
              useProcessingStore.getState().markComplete();
            },
            onError: ({ message: msg }) => {
              useProcessingStore.getState().appendFeedMessage({ time: getElapsed(), msg: `Error: ${msg}`, type: 'alert' });
              useProcessingStore.getState().markFailed(msg);
            },
          }, handleLiveStatus, handleLiveRecovery, undefined, true /* noRecovery — already replayed from DB */);
          // No `cancelRef.current?.()` preamble here: the enclosing `if`
          // already gated on `!cancelRef.current`, so it's guaranteed null.
          cancelRef.current = conn.disconnect;
        }
      } catch {
        restoredRef.current = false;  // restore failed — allow new-run path
        setIsRestoring(false);
        setRestoreChecked(true);
      }
    };

    tryRestore();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Wait for restore check to complete before deciding whether to start
    if (!restoreChecked) return;
    // Skip starting a new run if we restored from a previous session
    if (restoredRef.current) return;
    // Guard against duplicate starts (HMR re-mounts, React StrictMode)
    if (analysisStartedRef.current) return;
    analysisStartedRef.current = true;

    resetTimer();
    let cancelled = false;
    const abortController = new AbortController();
    const timers: ReturnType<typeof setTimeout>[] = [];

    const STAGE_MIN_DELAY = 800;
    const stageQueue: Array<() => void> = [];
    let stageProcessing = false;
    let lastStageTime = 0;

    function enqueueStageEvent(fn: () => void) {
      const now = Date.now();
      const elapsed = now - lastStageTime;
      if (elapsed >= STAGE_MIN_DELAY && !stageProcessing) {
        lastStageTime = now;
        fn();
      } else {
        stageQueue.push(fn);
        if (!stageProcessing) {
          stageProcessing = true;
          const delay = Math.max(STAGE_MIN_DELAY - elapsed, 50);
          timers.push(setTimeout(processStageQueue, delay));
        }
      }
    }

    function processStageQueue() {
      if (stageQueue.length === 0) {
        stageProcessing = false;
        return;
      }
      const fn = stageQueue.shift()!;
      lastStageTime = Date.now();
      fn();
      if (stageQueue.length > 0) {
        timers.push(setTimeout(processStageQueue, STAGE_MIN_DELAY));
      } else {
        stageProcessing = false;
      }
    }

    const demoMessages = FEED_MESSAGES;

    function runDemoFallback() {
      appendFeedMessage({ time: getElapsed(), msg: 'Running in demo mode...', type: 'warn' });

      const stageData = useProcessingStore.getState().stages;
      let cumulativeDelay = 0;

      stageData.forEach((stage, idx) => {
        timers.push(setTimeout(() => {
          advanceStage(idx);
          const steps = 20;
          const stepTime = stage.duration / steps;
          for (let s = 1; s <= steps; s++) {
            timers.push(setTimeout(() => {
              setStageProgress(idx, Math.round((s / steps) * 100));
            }, s * stepTime));
          }
        }, cumulativeDelay));

        timers.push(setTimeout(() => {
          completeStage(idx);
        }, cumulativeDelay + stage.duration));

        cumulativeDelay += stage.duration;
      });

      timers.push(setTimeout(() => markComplete(), cumulativeDelay));

      demoMessages.forEach((fm) => {
        timers.push(setTimeout(() => {
          appendFeedMessage({ time: getElapsed(), msg: fm.msg, type: fm.type });
        }, fm.delay));
      });

      timers.push(setTimeout(() => setCounters({ dataPoints: 47382 }), 3000));
      timers.push(setTimeout(() => setCounters({ insights: 156 }), 7000));
      timers.push(setTimeout(() => setCounters({ recommendations: 23 }), 11000));
    }

    function onAnySSEEvent() {
      gotSSEEvent.current = true;
    }

    async function startRun() {
      if (cancelled) return;
      try {
        // Guard: reconnect if there's already an active run for this user
        // (prevents duplicate pipelines).
        {
          // Same precedence as the restore path above: an explicit chat in the
          // URL is the run the user asked for, and must not be overridden by a
          // stale id left in the store or sessionStorage by an earlier audit.
          const existingChatId = urlChatId
            || useAuditStore.getState().chatId
            || sessionStorage.getItem('analysis_chat_id');
          if (existingChatId) {
            try {
              const existingStatus = await fetchAuditStatus(existingChatId);
              if (existingStatus.has_deliverables || existingStatus.has_renderable_report) {
                // Renderable dashboard exists — go straight to /report.
                navigate(`/report/${existingChatId}`, { replace: true });
                return;
              }
              if (existingStatus.is_complete && !existingStatus.is_running) {
                setChatId(existingChatId);
                navigate(`/report/${existingChatId}`, { replace: true });
                return;
              }
              if (existingStatus.is_running) {
                appendFeedMessage({ time: getElapsed(), msg: 'Reconnecting to running analysis...', type: 'highlight' });
                setChatId(existingChatId);
                window.history.replaceState(null, '', `/processing/${existingChatId}`);

                fastForwardStages(existingStatus.current_stage, existingStatus.current_total_stages);

                // Reconnect to live event stream
                if (import.meta.env.VITE_CENTRIFUGO_ENABLED === 'true') {
                  const conn = await createAnalysisConnection(
                    existingChatId,
                    {
                      onStageStarted: ({ stage_number, stage_name, total_stages }: { stage_number: number; stage_name: string; total_stages: number }) => {
                        onAnySSEEvent();
                        resetThinkingPulse();
                        const refine = isRefineStage(stage_name, total_stages);
                        advanceStage(combinedStageIndex(stage_number, refine));
                        appendFeedMessage({ time: getElapsed(), msg: refine ? stage_name : `Stage ${stage_number}: ${stage_name}`, type: 'highlight' });
                      },
                      onStageCompleted: ({ stage_number, stage_name }: { stage_number: number; stage_name: string }) => {
                        onAnySSEEvent();
                        resetThinkingPulse();
                        const refine = isRefineStage(stage_name);
                        const idx = combinedStageIndex(stage_number, refine);
                        if (refine) completeStageNoCounter(idx);
                        else completeStage(idx);
                        appendFeedMessage({ time: getElapsed(), msg: `✓ ${stage_name} complete`, type: 'highlight' });
                      },
                      onToolStart: ({ name, input }: { name: string; input?: Record<string, unknown> }) => {
                        onAnySSEEvent();
                        resetThinkingPulse();
                        appendFeedMessage({ time: getElapsed(), msg: toolStartMessage(name, input), type: '' });
                        const stages = useProcessingStore.getState().stages;
                        const idx = stages.findIndex((s) => s.status === 'active');
                        if (idx >= 0) {
                          useProcessingStore.getState().updateStageSubtask(idx, toolSubtaskLabel(name, input));
                        }
                      },
                      onToolResult: ({ name, result, is_error }: { name: string; result: string; is_error: boolean }) => {
                        onAnySSEEvent();
                        resetThinkingPulse();
                        const summary = summarizeToolResult(name, result, is_error);
                        if (summary) appendFeedMessage({ time: getElapsed(), msg: summary.msg, type: summary.type });
                        useProcessingStore.getState().incrementCounters(tallyToolResult(name, result, is_error));
                      },
                      onAnalysisRecovered: ({ stage_number }: { iteration: number; stage_number: number }) => {
                        onAnySSEEvent();
                        appendFeedMessage({
                          time: getElapsed(),
                          msg: `Analysis recovered — resuming from Stage ${stage_number}`,
                          type: 'highlight',
                        });
                      },
                      onText: () => { onAnySSEEvent(); },
                      onThinking: ({ content }: { content: string }) => {
                        onAnySSEEvent();
                        const snippet = handleThinkingChunk(content);
                        if (snippet) {
                          appendFeedMessage({ time: getElapsed(), msg: `… ${snippet}`, type: '' });
                        }
                      },
                      onDone: () => {
                        onAnySSEEvent();
                        appendFeedMessage({ time: getElapsed(), msg: '✓ Analysis complete', type: 'highlight' });
                        markComplete();
                      },
                      onError: ({ message: msg }: { message: string }) => {
                        onAnySSEEvent();
                        appendFeedMessage({ time: getElapsed(), msg: `Error: ${msg}`, type: 'alert' });
                        markFailed(msg);
                      },
                    },
                    handleLiveStatus, handleLiveRecovery,
                    existingStatus.last_seq > 0 ? existingStatus.last_seq : undefined,
                  );
                  cancelRef.current?.();
                  cancelRef.current = conn.disconnect;
                }
                return;
              }
            } catch { /* no existing run — proceed */ }
          }
        }
        if (cancelled) return;

        appendFeedMessage({ time: getElapsed(), msg: 'Initializing analysis...', type: '' });

        // Track last_seq to skip old Centrifugo history events from prior runs
        let minOffset = 0;

        // Adopt the chat onboarding already prepared. /collect created it and
        // filled its source_data; creating a fresh one here would start the
        // audit against an empty chat and re-fetch every platform. Only when
        // there is none (a direct /processing visit, or a second audit later)
        // do we mint one.
        const preparedChatId = useAuditStore.getState().collectChatId
          || (() => {
            try { return sessionStorage.getItem('collect_chat_id'); } catch { return null; }
          })();
        const chatId = preparedChatId
          || (await createConversation('Business Audit', abortController.signal)).id;
        if (cancelled) return;
        setChatId(chatId);
        sessionStorage.setItem('analysis_chat_id', chatId);
        setActiveAnalysis(chatId, OWNER_AUDIT_PROFILE);
        window.history.replaceState(null, '', `/processing/${chatId}`);

        // Save questionnaire answers (collected during onboarding) to DB.
        // The store keys by numeric Question.id; the backend persists by
        // string Question.field (business_stage, challenges, ...) so
        // downstream consumers (lead scorer, system prompts) can read by
        // name. See mapAnswersToBackendKeys for the conversion.
        const qAnswersById = useQuestionnaireStore.getState().answers;
        const qAnswers = {
          ...mapAnswersToBackendKeys(qAnswersById, QUESTIONS),
          // What the user confirmed on the Confirm step wins over anything the
          // legacy questionnaire left in the store — it is the newer answer and
          // the one the report is compiled against.
          ...(useOnboardingStore.getState().confirmedAnswers ?? {}),
        };
        // The confirmed profile rides along: when this is a freshly minted
        // chat rather than the adopted collection chat, it has none yet.
        // Awaited: the audit reads the profile from this chat when it starts,
        // so it must be written before startOwnerAudit (VIBE-2035).
        const confirmedProfile = useOnboardingStore.getState().confirmedProfile ?? undefined;
        if (Object.keys(qAnswers).length > 0 || confirmedProfile) {
          await saveQuestionnaire(chatId, qAnswers, confirmedProfile).catch(() => {});
        }
        if (cancelled) return;

        // The owner-audit route resolves connected sources + questionnaire
        // context server-side from the chat row, so the run message is fixed.
        const message = 'Run a full business audit.';

        appendFeedMessage({ time: getElapsed(), msg: 'Connecting to analysis engine...', type: '' });

        // ── Shared callbacks (used by both SSE and Centrifugo transports) ──
        // Dedup consecutive repeated tool events in the live feed
        const toolStartSeen = new Set<string>();
        const toolResultSeen = new Set<string>();

        const callbacks = {
          onStageStarted: ({ stage_number, stage_name, total_stages }: { stage_number: number; stage_name: string; total_stages: number }) => {
            onAnySSEEvent();
            resetThinkingPulse();
            // Reset dedup on new stage
            toolStartSeen.clear();
            toolResultSeen.clear();
            const refine = isRefineStage(stage_name, total_stages);
            const idx = combinedStageIndex(stage_number, refine);
            enqueueStageEvent(() => {
              advanceStage(idx);
              appendFeedMessage({
                time: getElapsed(),
                msg: refine ? stage_name : `Stage ${stage_number}: ${stage_name}`,
                type: 'highlight',
              });
            });
          },
          onStageCompleted: ({ stage_number, stage_name }: { stage_number: number; stage_name: string }) => {
            onAnySSEEvent();
            resetThinkingPulse();
            const refine = isRefineStage(stage_name);
            const idx = combinedStageIndex(stage_number, refine);
            enqueueStageEvent(() => {
              if (refine) completeStageNoCounter(idx);
              else completeStage(idx);
              appendFeedMessage({
                time: getElapsed(),
                msg: `✓ ${stage_name} complete`,
                type: 'highlight',
              });
            });
          },
          onToolStart: ({ name, input }: { name: string; input?: Record<string, unknown> }) => {
            onAnySSEEvent();
            resetThinkingPulse();
            // Dedup by name+args so repeated fetches with different params
            // still produce distinct feed lines (e.g. meta campaigns vs audience).
            const dedupKey = `${name}::${input ? JSON.stringify(input) : ''}`;
            // Activates "Data Ingestion" for pre-stage data tools so the panel
            // reflects ingestion/memory/data-load work that runs before the
            // backend announces stage 1 (~30s in).
            const currentStageIdx = activeStageIdxForTool(name);
            if (!toolStartSeen.has(dedupKey)) {
              toolStartSeen.add(dedupKey);
              appendFeedMessage({ time: getElapsed(), msg: toolStartMessage(name, input), type: '' });
            }
            if (currentStageIdx >= 0) {
              useProcessingStore.getState().updateStageSubtask(currentStageIdx, toolSubtaskLabel(name, input));
              const currentProgress = useProcessingStore.getState().stages[currentStageIdx].progress;
              setStageProgress(currentStageIdx, Math.min(currentProgress + 15, 95));
            }
          },
          onToolProgress: ({ name, message: msg }: { name: string; message: string }) => {
            onAnySSEEvent();
            const label = humanizeToolName(name);
            appendFeedMessage({ time: getElapsed(), msg: `${label}: ${msg}`, type: '' });
          },
          onToolResult: ({ name, result, is_error }: { name: string; result: string; is_error: boolean }) => {
            onAnySSEEvent();
            resetThinkingPulse();
            // Skip duplicate tool_result for same tool name within a stage (unless error)
            if (!is_error && toolResultSeen.has(name)) {
              // Still count for metrics even if we skip the feed message
            } else {
              if (!is_error) toolResultSeen.add(name);
              const summary = summarizeToolResult(name, result, is_error);
              if (summary) {
                appendFeedMessage({ time: getElapsed(), msg: summary.msg, type: summary.type });
              }
            }
            useProcessingStore.getState().incrementCounters(tallyToolResult(name, result, is_error));
          },
          onAnalysisRecovered: ({ stage_number }: { iteration: number; stage_number: number }) => {
            onAnySSEEvent();
            appendFeedMessage({
              time: getElapsed(),
              msg: `Analysis recovered — resuming from Stage ${stage_number}`,
              type: 'highlight',
            });
          },
          onText: () => {
            // Suppress LLM text output — stage transitions and tool events
            // provide sufficient progress info without flooding the feed
            onAnySSEEvent();
          },
          onThinking: ({ content }: { content: string }) => {
            onAnySSEEvent();
            // Emit a throttled snippet of the current reasoning so long
            // gaps between tool calls surface as progress in the feed.
            const snippet = handleThinkingChunk(content);
            if (snippet) {
              appendFeedMessage({ time: getElapsed(), msg: `… ${snippet}`, type: '' });
            }
          },
          onDone: () => {
            // Terminal `done` for the WHOLE run. The audit pass swallows its own
            // end-of-turn done server-side (terminal=False), so this only fires
            // after the refine pass — safe to treat as pipeline completion.
            onAnySSEEvent();
            while (stageQueue.length > 0) {
              stageQueue.shift()!();
            }
            stageProcessing = false;
            clearActiveAnalysis();
            appendFeedMessage({ time: getElapsed(), msg: '✓ Analysis complete', type: 'highlight' });
            markComplete();
          },
          onError: ({ message: msg }: { message: string }) => {
            onAnySSEEvent();
            appendFeedMessage({ time: getElapsed(), msg: `Error: ${msg}`, type: 'alert' });
            // Any backend `error` event is a non-recoverable terminal failure
            // (stalled stream, LLM error, agent-loop timeout). Surface a failed
            // panel instead of hanging on the spinner. The /status backstop
            // poll still auto-navigates if real deliverables turn up.
            clearActiveAnalysis();
            markFailed(msg);
          },
        };

        // ── Connect via Centrifugo (WebSocket) or SSE ──
        const useCentrifugo = import.meta.env.VITE_CENTRIFUGO_ENABLED === 'true';

        if (useCentrifugo) {
          // Get last_seq before starting — skip any old events recovered from Centrifugo history
          try {
            const status = await fetchAuditStatus(chatId);
            minOffset = status.last_seq || 0;
          } catch { /* new chat — no events yet */ }

          // Background trigger — the full pipeline (analysis → refine) runs
          // server-side under the owner profile; events stream via WebSocket.
          await startOwnerAudit(chatId);
          if (cancelled) return;

          const conn = await createAnalysisConnection(
            chatId, callbacks, handleLiveStatus, handleLiveRecovery,
            minOffset > 0 ? minOffset : undefined,
          );
          cancelRef.current?.();
          cancelRef.current = conn.disconnect;
        } else {
          // SSE mode — streaming HTTP response. The owner-audit start is
          // Centrifugo-first; SSE dev fallback still drives the same callbacks
          // via the legacy /api/chat endpoint with the owner profile.
          const token = useAuthStore.getState().token ?? undefined;
          const cancel = connectSSE(chatId, message, OWNER_AUDIT_PROFILE, callbacks, token, () => {
            onAnySSEEvent();
          });
          cancelRef.current?.();
          cancelRef.current = cancel;
        }

        timers.push(setTimeout(() => {
          if (!gotSSEEvent.current && !cancelled) {
            appendFeedMessage({
              time: getElapsed(),
              msg: 'No response from server. Switching to demo mode...',
              type: 'warn',
            });
            cancelRef.current?.();
            runDemoFallback();
          }
        }, SSE_SILENCE_TIMEOUT * 1000));

      } catch {
        if (!cancelled) {
          runDemoFallback();
        }
      }
    }

    startRun();

    return () => {
      cancelled = true;
      abortController.abort();
      cancelRef.current?.();
      cancelRef.current = null;
      gotSSEEvent.current = false;
      timers.forEach(clearTimeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restoreChecked]);

  // Pipeline complete → navigate to the report. `isFailed` is checked here
  // too, not just relied on via markComplete's own no-op-when-failed guard:
  // transport ordering isn't guaranteed, so a `done` can still land and set
  // isComplete=true BEFORE the `error` that follows sets isFailed=true. This
  // guard is the second, independent choke point that keeps that ordering
  // from sliding a failed run onto the success-path navigation.
  useEffect(() => {
    if (!isComplete || isFailed) return;
    const cid = useAuditStore.getState().chatId || urlChatId || '';
    const timer = setTimeout(() => navigate(`/report/${cid}`), 1500);
    return () => clearTimeout(timer);
  }, [isComplete, isFailed, navigate, urlChatId]);

  // Backstop: reconcile with /status so a missed live `done` event (backend
  // restart, recovery resume, Centrifugo history gap / WS reconnect) still
  // advances the UI. The live Centrifugo path stays primary; this only rescues
  // the missed-event case where the page would otherwise sit on a stage forever
  // until a manual refresh. Triggered by the transport's own gap signals (see
  // handleLiveStatus / handleLiveRecovery) rather than on a fixed interval.
  // Stops as soon as it navigates (view unmounts) or on cleanup.
  useEffect(() => {
    if (!restoreChecked) return;
    let stopped = false;
    const reconcile = async () => {
      if (stopped) return;
      const cid = useAuditStore.getState().chatId || urlChatId;
      if (!cid) return;
      try {
        const status = await fetchAuditStatus(cid);
        // Data ingestion produced nothing usable → the backend halted before the
        // report. Send the user back to the connect step to (re)connect sources
        // rather than leave them on a spinner / show an empty report.
        if (status.has_error && status.error_reason === 'no_data' && !status.is_running) {
          stopped = true;
          clearActiveAnalysis();
          try { sessionStorage.removeItem('analysis_chat_id'); } catch { /* ignore */ }
          useAuditStore.getState().setChatId('');
          useProcessingStore.getState().reset();
          navigate('/connect?audit_error=no_data', { replace: true });
          return;
        }
        // Route to /report once a renderable report / deliverables exist — this
        // covers the case where the live `done` was missed (Centrifugo history
        // gap, page opened after the run ended, or the loop completed without
        // persisting a `done` event).
        if (status.has_deliverables || status.has_renderable_report) {
          stopped = true;
          clearActiveAnalysis();
          navigate(`/report/${cid}`, { replace: true });
          return;
        }
        // Any other terminal failure (run errored, not running, produced no
        // report and it isn't the no_data case handled above) → stop the spinner
        // and surface the failed panel instead of polling forever.
        if (status.has_error && !status.is_running) {
          stopped = true;
          clearActiveAnalysis();
          useProcessingStore.getState().markFailed(
            status.error_reason
              ? `Analysis failed (${status.error_reason}).`
              : 'The analysis did not complete. Please try again.',
          );
          return;
        }
      } catch {
        /* transient — the next gap signal will try again */
      }
    };

    reconcileRef.current = () => { void reconcile(); };
    // One pass on mount: covers a page opened after the run already ended.
    void reconcile();

    // A hidden tab has its timers throttled and its socket may be dropped, so
    // re-check the moment the user comes back rather than trusting the feed.
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') void reconcile();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    // Floor for the one case with no event to react to: the socket is down, so
    // neither 'connected' nor 'subscribed' will fire until it returns.
    const id = setInterval(() => {
      if (liveStatusRef.current !== 'connected') void reconcile();
    }, OFFLINE_RECONCILE_INTERVAL_MS);

    return () => {
      stopped = true;
      reconcileRef.current = () => {};
      document.removeEventListener('visibilitychange', onVisibilityChange);
      clearInterval(id);
    };
  }, [restoreChecked, navigate, urlChatId]);

  // Overall progress across the combined pipeline. Completed stages count
  // whole; the running one contributes its own progress, so the bar keeps
  // creeping during a long stage instead of sitting still for minutes.
  const activeStage = stages.find((s) => s.status === 'active');
  const overallPercent = stages.length
    ? Math.min(100, Math.round(
        ((stages.filter((s) => s.status === 'completed').length
          + (activeStage ? Math.min(1, Math.max(0, activeStage.progress / 100)) : 0))
          / stages.length) * 100,
      ))
    : 0;
  // No active stage happens in two different states: before the run has
  // started anything (show 0 — nothing is done yet, "11 of 11" was a lie) and
  // between stages mid-run (show how many are done). The completed count is
  // right in both; the all-done state never reaches this label (isComplete
  // renders its own).
  const stageNumber = activeStage
    ? stages.indexOf(activeStage) + 1
    : stages.filter((st) => st.status === 'completed').length;

  return (
    <div className="mx-auto max-w-[1180px] px-4 py-8 sm:px-8">
      <div className="grid grid-cols-1 items-stretch gap-5 lg:grid-cols-[1.05fr_1fr]">
        <section
          ref={leftColRef}
          // Both columns share a floor so the terminal never towers over a
          // short stage list (the terminal's own min-height would win over the
          // measured height otherwise).
          className="flex min-h-[560px] flex-col overflow-hidden rounded-[18px] border bg-card shadow-sm"
        >
          <header className="flex items-center border-b bg-muted/40 px-7 py-4">
            <div className="inline-flex items-center gap-2 font-mono text-[10px] font-semibold uppercase tracking-[0.1em] text-secondary">
              <span className="ob-pulse size-1.5 rounded-full bg-secondary" />
              <span>
                {isComplete
                  ? 'Complete · ready to view'
                  : isFailed
                    ? 'Stopped'
                    : `Step ${stageNumber} of ${stages.length} · ${activeStage?.label ?? 'Starting'}`}
              </span>
            </div>
          </header>

          <div className="flex flex-col gap-6 px-7 pt-6">
            <div>
              <h1 className="mb-1.5 text-[23px] font-bold leading-tight tracking-[-0.025em] text-foreground">
                {isFailed
                  ? 'Analysis stopped'
                  : isRestoring
                    ? 'Restoring your analysis'
                    : 'Generating your business audit'}
              </h1>
              <p className="text-[13.5px] leading-relaxed text-muted-foreground">
                {isFailed
                  ? 'The analysis stopped before it could finish. You can start over — your connected sources are still linked.'
                  : isRestoring
                    ? 'Reconstructing analysis state from your previous session.'
                    : 'Our analysis engine is working through your data, cross-referencing category benchmarks and assembling the report.'}
              </p>
            </div>

            {isFailed && (
              <div className="rounded-lg border border-destructive/40 bg-destructive/10 p-4">
                <p className="mb-3 text-sm text-foreground">
                  {errorMessage || 'The analysis ended unexpectedly.'}
                </p>
                <button
                  type="button"
                  onClick={() => {
                    clearActiveAnalysis();
                    // Forget this run's chat entirely. "Start over" must mint a
                    // fresh one: the failed chat's idempotency key is already
                    // claimed (same chat + the fixed run message), so re-adopting
                    // it would return `already_started` and run nothing.
                    try {
                      sessionStorage.removeItem('analysis_chat_id');
                      sessionStorage.removeItem('collect_chat_id');
                    } catch { /* private mode */ }
                    useAuditStore.getState().setCollectChatId(null);
                    navigate('/connect');
                  }}
                  className="cursor-pointer rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90"
                >
                  Start over
                </button>
              </div>
            )}

            {/* Recovery banner intentionally suppressed: surfacing
                "backend restarted mid-analysis" to the lead caused
                confusion. Recovery still happens server-side and we
                keep the in-feed "resuming from Stage N" line — but
                the dismiss-able yellow banner is removed. */}

            <div className="flex flex-col gap-2.5">
              <div className="flex items-baseline justify-between font-mono text-[11px] uppercase tracking-[0.08em] text-muted-foreground">
                <span>Overall progress</span>
                <span className="text-[13px] font-semibold tabular-nums tracking-normal text-foreground">
                  {overallPercent}%
                </span>
              </div>
              <div
                role="progressbar"
                aria-valuenow={overallPercent}
                aria-valuemin={0}
                aria-valuemax={100}
                className="relative h-2 rounded-full bg-muted"
              >
                <span className="ob-bar__fill" style={{ width: `${overallPercent}%` }} />
                {!isComplete && !isFailed && (
                  <span className="ob-bar__head" style={{ right: `${100 - overallPercent}%` }} />
                )}
              </div>
            </div>

            <StageTimeline stages={stages} />
          </div>
        </section>

        <section
          style={terminalMaxH ? { height: terminalMaxH } : undefined}
          className="flex min-h-[560px] flex-col"
        >
          <TerminalFeed
            messages={feedMessages}
            isComplete={isComplete}
            model="vib3-engine"
            // Sources / Data points / Insights / Recommendations — the four
            // numbers the pipeline genuinely produces. (The source design also
            // showed a token count; there's no token signal on the Centrifugo
            // transport this screen uses, so it would read 0 on most runs.)
            stats={[
              { label: 'Sources', value: connectedCount },
              { label: 'Data points', value: counters.dataPoints },
              { label: 'Insights', value: counters.insights },
              { label: 'Actions', value: counters.recommendations },
            ]}
          />
        </section>
      </div>
    </div>
  );
}
